import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleSignInButton, PasswordSignInForm } from '@/shared/auth';
import { useBrand } from '@/shared/brand/useBrand';
import { PageHeader } from '@/shared/components/PageHeader';
import { LocaleSwitcher } from '@/shared/i18n/LocaleSwitcher';
import { useI18n } from '@/shared/i18n/useT';

/**
 * `/signin` — where the booking flow sends a client who needs an account.
 *
 * Not in the tab bar on purpose: nobody navigates here, they are sent here at
 * the end of picking their slots (docs/ARCHITECTURE.md §3).
 *
 * TODO: the 6-digit OTP form goes directly below the Google button, on this same
 * screen — not behind a "having trouble?" link. It is the only path that works
 * in an iOS standalone PWA, where the hop to Google can be handed to Safari.
 * See docs/GOOGLE-OAUTH.md §4.
 */
export function ClientSignIn() {
  const { t } = useI18n();
  const brand = useBrand();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [failed, setFailed] = useState(false);

  const title = t('auth.signIn.title');
  useEffect(() => {
    document.title = `${title} · ${brand.name}`;
  }, [title, brand.name]);

  // Where to return once signed in. Same-origin path only — an absolute URL here
  // would turn the sign-in screen into an open redirect.
  const next = params.get('next');
  const safeNext = next?.startsWith('/') && !next.startsWith('//') ? next : null;

  return (
    <>
      <PageHeader
        title={title}
        description={t('auth.signIn.description')}
        actions={<LocaleSwitcher compact />}
      />

      <div className="mx-auto mt-6 w-full max-w-sm">
        <GoogleSignInButton
          {...(safeNext === null ? {} : { next: safeNext })}
          promptSelectAccount
          onError={() => setFailed(true)}
        />

        {failed && (
          <p role="alert" className="mt-3 text-sm text-danger">
            {t('auth.google.error')}
          </p>
        )}

        {/* Password sits below Google, not behind a link: while the consent
            screen is still unverified it is the only path that works at all. */}
        <div className="mt-6 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-border" />
          <span className="text-xs uppercase tracking-wide text-muted">
            {t('auth.password.or')}
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <PasswordSignInForm
          onSignedIn={() => navigate(safeNext ?? '/me/bookings', { replace: true })}
        />
      </div>
    </>
  );
}
